// Spatial Manifold: one chosen edge of the board pushed out a square (§31 C7).

import {
  COORD_MAX,
  COORD_MIN,
  type Direction,
  EAST,
  fileOf,
  NORTH,
  rankOf,
  type Side,
  SOUTH,
  square
} from "../board";
import { isAttackedByEnemyOf } from "../chess/attacks";
import { hasStructure, type IndustryState } from "../industry/state";
import { bindingRoyal, clonePosition, type Position, setCells } from "../position";

/** Why the manifold cannot be opened that way, or null. */
export type ManifoldRefusal = "edge-of-the-world" | "nothing-to-grow" | "opens-a-line";

/** North and south push along the ranks; east and west along the files. */
const alongRanks = (direction: Direction): boolean =>
  direction === NORTH || direction === SOUTH;

/** North and east take the larger coordinate; south and west the smaller. */
const outward = (direction: Direction): boolean =>
  direction === NORTH || direction === EAST;

/**
 * The squares this direction would add, or the packing's edge.
 *
 * **Per column, never per bounding box.** The designer's words are *the spaces
 * which define the edge*, so each file (or rank) grows from **its own**
 * outermost cell and a file holding none grows nothing — the frontier is pushed
 * out one and ragged stays ragged. `dev-grow`'s edge builder does the other
 * thing, sweeping the box and laying a straight row over files that may be
 * empty, which is right for a dev tool asking for a rectangle and wrong here.
 *
 * Built from `fileOf`/`rankOf`/`square` rather than by adding `DIR_OFFSET` to
 * an edge square, for `bridges.ts`'s reason: a packed offset wraps at the file
 * band, so `+1` on file 511 silently lands in the next rank.
 */
export function manifoldSquares(
  position: Position,
  industry: IndustryState,
  direction: Direction
): number[] | "edge-of-the-world" {
  const ranks = alongRanks(direction);
  const out = outward(direction);
  // Key is the coordinate the edge runs *across*; value is the outermost cell's
  // coordinate along the push. Swept over the sorted `cellList`, so the keys
  // arrive in board order and nothing here depends on Map iteration luck.
  const edge = new Map<number, number>();
  for (const sq of position.cellList) {
    const key = ranks ? fileOf(sq) : rankOf(sq);
    const along = ranks ? rankOf(sq) : fileOf(sq);
    const seen = edge.get(key);
    if (seen === undefined || (out ? along > seen : along < seen)) edge.set(key, along);
  }

  const grown: number[] = [];
  for (const key of [...edge.keys()].sort((a, b) => a - b)) {
    const along = edge.get(key)! + (out ? 1 : -1);
    // Past the packing the square is not merely off the map, it is a *different*
    // square in another rank band — silent corruption rather than a bad move, so
    // the whole direction goes rather than the column being skipped.
    if (along < COORD_MIN || along > COORD_MAX) return "edge-of-the-world";
    const at = ranks ? square(key, along) : square(along, key);
    // A building stands on a cell (§31 S1), so it is part of the edge and
    // the square beyond it is never one. Kept as a guard for a state from
    // outside the reducer — a structure off the floor is not ground to lay.
    if (hasStructure(industry, at)) continue;
    grown.push(at);
  }
  return grown;
}

/**
 * Every rule about the push, in one place so the card and the board agree.
 *
 * **The self-check is here rather than after the fact**, which is the one place
 * this differs from `pave`. Adding a cell *lengthens* rays, so growing the
 * board can open a line onto your own king, where the Bomb's deletion can only
 * ever take attacks away. Every rule lives in `refuse`, so the card cannot be
 * offered when the reducer would turn it down.
 */
export function refuseManifold(
  position: Position,
  industry: IndustryState,
  side: Side,
  direction: Direction
): ManifoldRefusal | null {
  const grown = manifoldSquares(position, industry, direction);
  if (grown === "edge-of-the-world") return grown;
  if (grown.length === 0) return "nothing-to-grow";

  // A side holding a spare royal is unconstrained here exactly as the move
  // filter leaves it: `bindingRoyal` answers -1 and there is nothing to expose.
  const king = bindingRoyal(position, side);
  if (king < 0) return null;
  // On a clone, because `refuse` is asked before anything is played.
  const probe = clonePosition(position);
  setCells(probe, [...probe.cellList, ...grown]);
  return isAttackedByEnemyOf(probe, king, side) ? "opens-a-line" : null;
}
